import { checkAuth } from '@libs/localStorage'
import {
  TPromotionCampaign,
  TPostPromotionCampaign,
  TPatchPromotionCampaign,
  TQueryPromotionCampaign,
  TUpdatePromotionCampaignStatus,
  TPromotionCampaignReport,
} from '@src/modules/promotion-campaign'
import {
  createPromotionCampaign,
  deletePromotionCampaign,
  getListPromotionCampaign,
  getPromotionCampaignById,
  getPromotionCampaignReport,
  updatePromotionCampaign,
  updatePromotionCampaignStatus,
} from '@src/queries/apis/promotion-campaign'
import { useMutation, useQuery, useQueryClient } from 'react-query'

// Query keys
export const PROMOTION_CAMPAIGN_KEYS = {
  all: ['promotion-campaign'] as const,
  lists: () => [...PROMOTION_CAMPAIGN_KEYS.all, 'list'] as const,
  list: (filters: TQueryPromotionCampaign) => [...PROMOTION_CAMPAIGN_KEYS.lists(), filters] as const,
  details: () => [...PROMOTION_CAMPAIGN_KEYS.all, 'detail'] as const,
  detail: (id: number) => [...PROMOTION_CAMPAIGN_KEYS.details(), id] as const,
  reports: () => [...PROMOTION_CAMPAIGN_KEYS.all, 'report'] as const,
  report: (id: number) => [...PROMOTION_CAMPAIGN_KEYS.reports(), id] as const,
}

// Hook để lấy danh sách chiến dịch
export const useQueryListPromotionCampaign = (params: TQueryPromotionCampaign) => {
  const token = checkAuth()
  return useQuery(
    PROMOTION_CAMPAIGN_KEYS.list(params),
    () => getListPromotionCampaign(params, token || ''),
    {
      enabled: !!token,
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  )
}

// Hook để lấy chi tiết chiến dịch
export const useQueryPromotionCampaignDetail = (id: number) => {
  const token = checkAuth()
  return useQuery(
    PROMOTION_CAMPAIGN_KEYS.detail(id),
    () => getPromotionCampaignById(id, token || ''),
    {
      enabled: !!token && !!id,
      staleTime: 5 * 60 * 1000,
    },
  )
}

// Hook để lấy báo cáo chiến dịch
export const useQueryPromotionCampaignReport = (id: number) => {
  const token = checkAuth()
  return useQuery(
    PROMOTION_CAMPAIGN_KEYS.report(id),
    () => getPromotionCampaignReport(id, token || ''),
    {
      enabled: !!token && !!id,
      staleTime: 5 * 60 * 1000,
    },
  )
}

// Hook để tạo chiến dịch mới
export const useMutationCreatePromotionCampaign = () => {
  const queryClient = useQueryClient()
  const token = checkAuth()

  return useMutation(
    (data: TPostPromotionCampaign) => createPromotionCampaign(data, token || ''),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(PROMOTION_CAMPAIGN_KEYS.lists())
      },
    },
  )
}

// Hook để cập nhật chiến dịch
export const useMutationUpdatePromotionCampaign = () => {
  const queryClient = useQueryClient()
  const token = checkAuth()

  return useMutation(
    ({ id, data }: { id: number; data: TPatchPromotionCampaign }) =>
      updatePromotionCampaign(id, data, token || ''),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(PROMOTION_CAMPAIGN_KEYS.lists())
        queryClient.invalidateQueries(PROMOTION_CAMPAIGN_KEYS.detail(variables.id))
      },
    },
  )
}

// Hook để thay đổi trạng thái chiến dịch
export const useMutationUpdatePromotionCampaignStatus = () => {
  const queryClient = useQueryClient()
  const token = checkAuth()

  return useMutation(
    ({ id, data }: { id: number; data: TUpdatePromotionCampaignStatus }) =>
      updatePromotionCampaignStatus(id, data, token || ''),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(PROMOTION_CAMPAIGN_KEYS.lists())
        queryClient.invalidateQueries(PROMOTION_CAMPAIGN_KEYS.detail(variables.id))
      },
    },
  )
}

// Hook để xóa chiến dịch
export const useMutationDeletePromotionCampaign = () => {
  const queryClient = useQueryClient()
  const token = checkAuth()

  return useMutation((id: number) => deletePromotionCampaign(id, token || ''), {
    onSuccess: () => {
      queryClient.invalidateQueries(PROMOTION_CAMPAIGN_KEYS.lists())
    },
  })
}
